// The retainer's contract term, as the screens show it: the two dates, and how close
// the end is. Shared by the retainers list and the retainer screen — renewal is
// something you scan a list for and then act on inside one record, so both need it.
//
// Every figure here is computed on the SERVER (calculations/retainerTerm.js) and
// arrives on `term`. Nothing in this file decides when a retainer has ended or is
// near its end; a browser clock disagreeing with the server about whether a contract
// is live is exactly the bug the thin-client rule exists to prevent.
import he from '../i18n/he';
import DateText from './DateText';

// Is this retainer still expecting to bill? Everything in this file that COUNTS DOWN or
// PROMPTS is addressed to a live engagement, and says nothing to a closed one: a
// cancelled retainer with "נותרו 12 ימים" beside it is asking about a future that was
// called off, and "יש לחדש את ההסכם" on a completed one is asking for a decision that
// has already been made. Closed retainers show their dates and nothing more.
//
// `status` is OPTIONAL, and its absence means live. Callers that only ever render active
// retainers (the client card's work tree) do not have to pass it, and the default is the
// behaviour every caller had before closing existed.
//
// Note which way this runs: a closed status silences the badges, but a passed end_date
// never sets the status. `term.ended` on an ACTIVE retainer is exactly the red badge
// below, and it stays — that prompt is how a lapsed contract gets noticed at all.
const isLive = (status) => status == null || status === 'active';

// The state of the term, when there is something to say about it. Renders nothing for
// a retainer comfortably inside its term or one with no end date at all — a badge on
// every row is a badge nobody reads.
export const TermBadge = ({ term, status }) => {
  if (!term || !isLive(status)) return null;
  if (term.ended) {
    const days = term.daysRemaining != null ? Math.abs(term.daysRemaining) : null;
    return (
      <span className="badge red" title={days != null ? he.retainers.termEndedAgo(days) : undefined}>
        {he.retainers.termEnded}
      </span>
    );
  }
  if (term.nearEnd) {
    return (
      <span className="badge amber">
        {term.daysRemaining === 0
          ? he.retainers.termEndsToday
          : he.retainers.termEndsIn(term.daysRemaining)}
      </span>
    );
  }
  return null;
};

// The quiet half of the same fact: how much of the term is left. It renders exactly
// where TermBadge renders nothing — a row comfortably inside its term, which until now
// showed a date and no sense of how far off it was. Where the badge does speak, this
// stays silent: an amber "נותרו 41 ימים" beside a plain one says it twice, and urgency
// is the badge's job alone.
//
// An open-ended retainer shows nothing. `daysRemaining` is NULL there, not 0 — the
// server never counts down to a date that does not exist — and the endDate guard above
// this one already returns before we reach it. The `< 0` case cannot arrive either
// (a past end date is `ended`), and is refused rather than trusted: a negative count
// read as "remaining" is the one way this could print a wrong number.
const TermRemaining = ({ term, status }) => {
  if (!isLive(status)) return null;
  if (term.ended || term.nearEnd) return null;
  if (term.daysRemaining == null || term.daysRemaining < 0) return null;
  return <span className="muted">{he.retainers.termEndsIn(term.daysRemaining)}</span>;
};

// One table cell: when the term ends, and how long is left — as a badge once that is
// urgent, as plain text while it is not. The end alone, not the full range — a list is
// scanned for what is about to run out.
export const TermCell = ({ term, status }) => {
  if (!term || !term.endDate) return <span className="muted">{he.retainers.termOpenEnded}</span>;
  return (
    <>
      <DateText value={term.endDate} />{' '}
      <TermBadge term={term} status={status} />
      <TermRemaining term={term} status={status} />
    </>
  );
};

// The full term for the retainer screen: start – end, the month count and the state badge.
export const TermLine = ({ term, status }) => {
  if (!term) return null;
  const { startDate, endDate } = term;
  if (!startDate && !endDate) {
    return <p className="muted">{he.retainers.termNoDates}</p>;
  }
  return (
    <p className="muted">
      {he.retainers.term}:{' '}
      <strong>{startDate ? <DateText value={startDate} /> : '—'}</strong>
      {' – '}
      <strong>{endDate ? <DateText value={endDate} /> : he.retainers.termOpenEnded}</strong>
      {term.months != null && <> · {term.months} {he.retainers.monthsUnit}</>}
      {' '}<TermBadge term={term} status={status} />
    </p>
  );
};

export default TermLine;
